'use client';

import { useEffect, useState } from 'react';
import { useUsersService, UserDto, CreateUserRequest, UpdateUserRequest } from '@/app/admin-tools/api/users';
import { Link } from 'react-router-dom';
import {
  ChevronRightIcon,
  HomeIcon,
  PencilIcon,
  TrashIcon,
  ShieldCheckIcon,
  ShieldExclamationIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  UserIcon,
  UserGroupIcon,
  Cog6ToothIcon,
  WrenchScrewdriverIcon
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../AdminLayout';
import Tooltip from '@/components/common/Tooltip';
import ConfirmDialog from '@/components/common/ConfirmDialog';

export default function UsersAdminPage() {
  const usersService = useUsersService();
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentUser, setCurrentUser] = useState<UserDto | null>(null);
  const [selectedUser, setSelectedUser] = useState<UserDto | null>(null);
  const [formData, setFormData] = useState<{
    email: string;
    password: string;
    display_name: string;
    is_system_admin: boolean;
  }>({
    email: '',
    password: '',
    display_name: '',
    is_system_admin: false,
  });
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [actionInProgress, setActionInProgress] = useState(false);
  const [isDoModalOpen, setIsDoModalOpen] = useState(false);
  const [selectedUserForDos, setSelectedUserForDos] = useState<UserDto | null>(null);
  const [userDos, setUserDos] = useState<{ name: string; description: string }[]>([]);
  const [doState, setDoState] = useState<{ inMemoryState: any; persistedState: any } | null>(null);
  const [doStateLoading, setDoStateLoading] = useState(false);
  const [confirmDoReset, setConfirmDoReset] = useState<string | null>(null);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [selectedUserForSettings, setSelectedUserForSettings] = useState<UserDto | null>(null);
  const [settingsJsonString, setSettingsJsonString] = useState('');

  // Load users on initial render
  useEffect(() => {
    fetchUsers();
    fetchCurrentUser();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await usersService.getAllUsers();
      setUsers(data);
    } catch (err) {
      setError('Failed to load users. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const data = await usersService.getCurrentUser();
      setCurrentUser(data);
    } catch (err) {
      console.error('Failed to load current user:', err);
    }
  };

  const handleCreateUser = async () => {
    try {
      setActionInProgress(true);
      setError(null);

      // Validate required fields
      if (!formData.email || !formData.password) {
        setError('Email and password are required.');
        return;
      }

      const createRequest: CreateUserRequest = {
        email: formData.email,
        password: formData.password,
        display_name: formData.display_name || undefined,
        is_system_admin: formData.is_system_admin,
      };

      const newUser = await usersService.createUser(createRequest);

      // Update the users list
      setUsers(prevUsers => [...prevUsers, newUser]);

      // Reset form and close modal
      setFormData({
        email: '',
        password: '',
        display_name: '',
        is_system_admin: false,
      });
      setIsCreating(false);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to create user. Please try again.');
      console.error(err);
    } finally {
      setActionInProgress(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;

    try {
      setActionInProgress(true);
      setError(null);

      const updateData: UpdateUserRequest = {
        display_name: formData.display_name || undefined,
        is_system_admin: formData.is_system_admin,
      };

      // Only include password if it was provided
      if (formData.password) {
        updateData.password = formData.password;
      }

      const updatedUser = await usersService.updateUser(selectedUser.id, updateData);

      // Update the users list
      setUsers(prevUsers =>
        prevUsers.map(user =>
          user.id === updatedUser.id ? updatedUser : user
        )
      );

      // Reset form and close modal
      setIsEditing(false);
      setSelectedUser(null);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to update user. Please try again.');
      console.error(err);
    } finally {
      setActionInProgress(false);
    }
  };

  const handleDeleteUser = async (id: string) => {
    try {
      setActionInProgress(true);
      setError(null);

      await usersService.deleteUser(id);

      // Update the users list
      setUsers(prevUsers => prevUsers.filter(user => user.id !== id));

      // Reset confirmation
      setConfirmDelete(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete user. Please try again.');
      console.error(err);
    } finally {
      setActionInProgress(false);
    }
  };

  const handleSetAdminStatus = async (id: string, isAdmin: boolean) => {
    try {
      setActionInProgress(true);
      setError(null);

      const updatedUser = await usersService.setAdminStatus(id, isAdmin);

      // Update the users list
      setUsers(prevUsers =>
        prevUsers.map(user =>
          user.id === updatedUser.id ? updatedUser : user
        )
      );
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update admin status. Please try again.');
      console.error(err);
    } finally {
      setActionInProgress(false);
    }
  };

  const handleSetUserType = async (id: string, userType: 'personal' | 'business') => {
    try {
      setActionInProgress(true);
      setError(null);

      const updatedUser = await usersService.setUserType(id, userType);

      // Update the users list
      setUsers(prevUsers =>
        prevUsers.map(user =>
          user.id === updatedUser.id ? updatedUser : user
        )
      );
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update user type. Please try again.');
      console.error(err);
    } finally {
      setActionInProgress(false);
    }
  };

  const handleManageDosClick = async (user: UserDto) => {
    setSelectedUserForDos(user);
    setIsDoModalOpen(true);
    setDoState(null);
    try {
      const dos = await usersService.getManageableDos(user.id);
      setUserDos(dos);
    } catch (err) {
      setError('Failed to load Durable Objects list.');
      console.error(err);
    }
  };

  const handleViewDoState = async (doName: string) => {
    if (!selectedUserForDos) return;
    setDoStateLoading(true);
    setDoState(null);
    try {
      const state = await usersService.getDoState(selectedUserForDos.id, doName);
      setDoState(state);
    } catch (err) {
      setError(`Failed to get state for ${doName}.`);
      console.error(err);
    } finally {
      setDoStateLoading(false);
    }
  };

  const handleResetDo = async (doName: string) => {
    if (!selectedUserForDos) return;
    try {
      await usersService.resetDo(selectedUserForDos.id, doName);
      // Re-fetch state to show it has been cleared
      await handleViewDoState(doName);
      setConfirmDoReset(null);
    } catch (err) {
      setError(`Failed to reset ${doName}.`);
      console.error(err);
    }
  };

  const handleEditClick = (user: UserDto) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      display_name: user.display_name || '',
      is_system_admin: user.is_system_admin,
      password: '' // Always clear password on edit
    });
    setIsEditing(true);
  };

  const handleOpenSettingsModal = (user: UserDto) => {
    setSelectedUserForSettings(user);
    // Pretty-print the JSON for readability, handling null or undefined settings
    setSettingsJsonString(JSON.stringify(user.settings || {}, null, 2));
    setIsSettingsModalOpen(true);
  };

  const handleSaveSettings = async () => {
    if (!selectedUserForSettings) return;

    try {
      const newSettings = JSON.parse(settingsJsonString);
      setActionInProgress(true);
      setError(null);

      const updatedUser = await usersService.updateUserSettings(selectedUserForSettings.id, newSettings);

      setUsers(prevUsers =>
        prevUsers.map(user =>
          user.id === updatedUser.id ? updatedUser : user
        )
      );

      setIsSettingsModalOpen(false);
      setSelectedUserForSettings(null);
    } catch (err: any) {
      if (err instanceof SyntaxError) {
        setError('Invalid JSON format. Please check your syntax.');
      } else {
        setError(err.response?.data?.message || 'Failed to update settings.');
      }
      console.error(err);
    } finally {
      setActionInProgress(false);
    }
  };

  const filteredUsers = users.filter(user => {
    const searchLower = searchTerm.toLowerCase();
    return (
      user.email.toLowerCase().includes(searchLower) ||
      (user.display_name && user.display_name.toLowerCase().includes(searchLower))
    );
  });

  return (
    <AdminLayout>
      {/* Breadcrumb Navigation */}
      <nav className="flex mb-6" aria-label="Breadcrumb">
        <ol className="inline-flex items-center space-x-1 md:space-x-3">
          <li className="inline-flex items-center">
            <Link to="/admin-tools" className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-blue-600">
              <HomeIcon className="w-4 h-4 mr-2" />
              Admin Tools
            </Link>
          </li>
          <li>
            <div className="flex items-center">
              <ChevronRightIcon className="w-4 h-4 text-gray-400" />
              <span className="ml-1 text-sm font-medium text-gray-500 md:ml-2">User Management</span>
            </div>
          </li>
        </ol>
      </nav>

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">User Management</h1>
        <button
          onClick={() => setIsCreating(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add User
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded-md mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <XMarkIcon className="h-5 w-5 text-red-500" />
            </div>
            <div className="ml-3">
              <p className="text-sm">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search users by email or name"
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          />
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white shadow overflow-hidden rounded-lg">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Login
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 whitespace-nowrap">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
                    </div>
                  </td>
                </tr>
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 whitespace-nowrap text-center text-gray-500">
                    {searchTerm ? 'No users found matching your search.' : 'No users found.'}
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-gray-200 rounded-full flex items-center justify-center">
                          <UserIcon className="h-6 w-6 text-gray-500" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {user.display_name || 'N/A'}
                          </div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user.is_system_admin ? (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">
                          Admin
                        </span>
                      ) : (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                          User
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.last_login_at ? format(new Date(Number(user.last_login_at) * 1000), 'MMM d, yyyy') : 'Never'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {format(new Date(Number(user.created_at) * 1000), 'MMM d, yyyy')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        {confirmDelete === user.id ? (
                          <>
                            <button
                              onClick={() => handleDeleteUser(user.id)}
                              disabled={actionInProgress || currentUser?.id === user.id}
                              className="text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Confirm
                            </button>
                            <button
                              onClick={() => setConfirmDelete(null)}
                              className="text-gray-600 hover:text-gray-900"
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <Tooltip content="Edit user">
                              <button
                                onClick={() => handleEditClick(user)}
                                className="text-blue-600 hover:text-blue-900"
                              >
                                <PencilIcon className="h-5 w-5" />
                              </button>
                            </Tooltip>
                            <Tooltip content={user.is_system_admin ? 'Remove admin rights' : 'Make admin'}>
                              <button
                                onClick={() => handleSetAdminStatus(user.id, !user.is_system_admin)}
                                disabled={actionInProgress || currentUser?.id === user.id}
                                className={`${user.is_system_admin ? 'text-green-600 hover:text-green-900' : 'text-gray-600 hover:text-gray-900'
                                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                              >
                                {user.is_system_admin ? (
                                  <ShieldCheckIcon className="h-5 w-5" />
                                ) : (
                                  <ShieldExclamationIcon className="h-5 w-5" />
                                )}
                              </button>
                            </Tooltip>
                            <Tooltip content="Manage Durable Objects">
                              <button
                                onClick={() => handleManageDosClick(user)}
                                className="text-gray-600 hover:text-gray-900"
                              >
                                <Cog6ToothIcon className="h-5 w-5" />
                              </button>
                            </Tooltip>
                            <Tooltip content="Edit User Settings">
                              <button
                                onClick={() => handleOpenSettingsModal(user)}
                                className="text-gray-600 hover:text-gray-900"
                              >
                                <WrenchScrewdriverIcon className="h-5 w-5" />
                              </button>
                            </Tooltip>
                            <Tooltip content="Delete user">
                              <button
                                onClick={() => setConfirmDelete(user.id)}
                                disabled={actionInProgress || currentUser?.id === user.id}
                                className="text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            </Tooltip>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create User Modal */}
      {isCreating && (
        <div className="fixed inset-0 overflow-y-auto z-50">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Create New User</h3>
                    <div className="mt-2 space-y-4">
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                          Email <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          id="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                          Password <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="password"
                          id="password"
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="displayName" className="block text-sm font-medium text-gray-700">
                          Display Name
                        </label>
                        <input
                          type="text"
                          id="displayName"
                          value={formData.display_name}
                          onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="isAdmin"
                          checked={formData.is_system_admin}
                          onChange={(e) => setFormData({ ...formData, is_system_admin: e.target.checked })}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="isAdmin" className="ml-2 block text-sm text-gray-900">
                          Admin User
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleCreateUser}
                  disabled={actionInProgress}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {actionInProgress ? 'Creating...' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={() => setIsCreating(false)}
                  disabled={actionInProgress}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {isEditing && selectedUser && (
        <div className="fixed inset-0 overflow-y-auto z-50">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Edit User: {selectedUser.email}</h3>
                    <div className="mt-2 space-y-4">
                      <div>
                        <label htmlFor="display_name" className="block text-sm font-medium text-gray-700">
                          Display Name
                        </label>
                        <input
                          type="text"
                          id="display_name"
                          value={formData.display_name}
                          onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                          Password (leave blank to keep current password)
                        </label>
                        <input
                          type="password"
                          id="password"
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          placeholder="Enter new password"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          This will update the user's password in Firebase. Leave blank to keep the current password.
                        </p>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="is_system_admin_edit"
                          checked={formData.is_system_admin}
                          onChange={(e) => setFormData({ ...formData, is_system_admin: e.target.checked })}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          disabled={currentUser?.id === selectedUser.id}
                        />
                        <label htmlFor="is_system_admin_edit" className="ml-2 block text-sm text-gray-900">
                          Admin User
                          {currentUser?.id === selectedUser.id && (
                            <span className="text-xs text-gray-500 ml-2">(Cannot change your own admin status)</span>
                          )}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleUpdateUser}
                  disabled={actionInProgress}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {actionInProgress ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsEditing(false);
                    setSelectedUser(null);
                  }}
                  disabled={actionInProgress}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {selectedUserForDos && (
        <DurableObjectManagerModal
          user={selectedUserForDos}
          isOpen={isDoModalOpen}
          onClose={() => setIsDoModalOpen(false)}
          dos={userDos}
          doState={doState}
          doStateLoading={doStateLoading}
          onView={handleViewDoState}
          onResetConfirm={(doName) => setConfirmDoReset(doName)}
        />
      )}

      {isSettingsModalOpen && selectedUserForSettings && (
        <div className="fixed inset-0 overflow-y-auto z-50">
          <div className="flex items-center justify-center min-h-screen">
            <div className="fixed inset-0 bg-gray-500 opacity-75"></div>
            <div className="bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-2xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  Edit Settings for {selectedUserForSettings.email}
                </h3>
                {error && (
                  <p className="text-sm text-red-600 mb-2">{error}</p>
                )}
                <textarea
                  value={settingsJsonString}
                  onChange={(e) => setSettingsJsonString(e.target.value)}
                  className="w-full h-64 p-2 border border-gray-300 rounded-md font-mono text-sm"
                  placeholder='Enter user settings as a JSON object...'
                />
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleSaveSettings}
                  disabled={actionInProgress}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  {actionInProgress ? 'Saving...' : 'Save Settings'}
                </button>
                <button
                  type="button"
                  onClick={() => setIsSettingsModalOpen(false)}
                  disabled={actionInProgress}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedUserForDos && confirmDoReset && (
        <ConfirmDialog
          isOpen={true}
          onClose={() => setConfirmDoReset(null)}
          onConfirm={() => handleResetDo(confirmDoReset)}
          title={`Reset ${confirmDoReset}?`}
          message={`Are you sure you want to reset the ${confirmDoReset} for ${selectedUserForDos.email}? This action cannot be undone.`}
          confirmText="Reset Object"
        />
      )}
    </AdminLayout>
  );
}

interface DurableObjectManagerModalProps {
  user: UserDto;
  isOpen: boolean;
  onClose: () => void;
  dos: { name: string; description: string }[];
  doState: { inMemoryState: any; persistedState: any } | null;
  doStateLoading: boolean;
  onView: (doName: string) => void;
  onResetConfirm: (doName: string) => void;
}

function DurableObjectManagerModal({ user, isOpen, onClose, dos, doState, doStateLoading, onView, onResetConfirm }: DurableObjectManagerModalProps) {
  const [selectedDoName, setSelectedDoName] = useState<string | null>(null);

  const handleSelectDo = (doName: string) => {
    setSelectedDoName(doName);
    onView(doName);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 overflow-y-auto z-[60]">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true" onClick={onClose}>
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 relative">
            <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-500">
              <XMarkIcon className="h-6 w-6" />
            </button>
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Manage Durable Objects for {user.email}</h3>
                <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="space-y-4 md:col-span-1">
                    <h4 className="font-medium text-gray-800">Available Objects</h4>
                    <ul className="divide-y divide-gray-200 border border-gray-200 rounded-md">
                      {dos.map(d => (
                        <li
                          key={d.name}
                          onClick={() => handleSelectDo(d.name)}
                          className={`p-3 cursor-pointer hover:bg-gray-50 ${selectedDoName === d.name ? 'bg-blue-50' : ''
                            }`}
                        >
                          <p className="text-sm font-medium text-gray-900">{d.name}</p>
                          <p className="text-sm text-gray-500">{d.description}</p>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="space-y-4 md:col-span-2">
                    <div className="flex justify-between items-center">
                      <h4 className="font-medium text-gray-800">Object State</h4>
                      <button
                        onClick={() => onResetConfirm(selectedDoName!)}
                        disabled={!selectedDoName}
                        className="text-sm text-red-600 hover:text-red-800 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Reset
                      </button>
                    </div>
                    <div className="h-96 space-y-2">
                      <div className="bg-gray-100 rounded-md p-4 h-1/2 overflow-auto">
                        <h5 className="text-xs font-semibold text-gray-500 mb-2">In-Memory State</h5>
                        {doStateLoading ? (
                          <div className="flex justify-center items-center h-full">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                          </div>
                        ) : (
                          <pre className="text-sm whitespace-pre-wrap"><code>{doState ? JSON.stringify(doState.inMemoryState, null, 2) : 'Select an object to view its state.'}</code></pre>
                        )}
                      </div>
                      <div className="bg-gray-800 text-white rounded-md p-4 h-1/2 overflow-auto">
                        <h5 className="text-xs font-semibold text-gray-400 mb-2">Persisted State</h5>
                        {doStateLoading ? (
                          <div className="flex justify-center items-center h-full">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                          </div>
                        ) : (
                          <pre className="text-sm whitespace-pre-wrap"><code>{doState ? JSON.stringify(doState.persistedState, null, 2) : ''}</code></pre>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={onClose}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
